if (!customElements.get('money-multiplier-calculator')) {
  class MoneyMultiplierCalculator extends HTMLElement {
    connectedCallback() {
      this.steps = JSON.parse(this.dataset.steps ?? '[]');
      this.months = Number(this.dataset.months ?? 6);
      this.bonusRate = Number(this.dataset.bonusRate ?? 30);
      this.redeemCards = [...this.querySelectorAll('[data-redeem-card]')];

      this.rangeInput = this.querySelector('[data-range]');
      this.amountDisplay = this.querySelector('[data-amount]');
      this.donutPay = this.querySelector('[data-donut-pay]');
      this.totalPaid = this.querySelector('[data-total-paid]');
      this.bonusTitle = this.querySelector('[data-bonus-title]');
      this.bonusNote = this.querySelector('[data-bonus-note]');
      this.bonusAmount = this.querySelector('[data-bonus-amount]');
      this.grandTotal = this.querySelector('[data-grand-total]');

      if (this.rangeInput) {
        this.handleSliderInput = this.handleSliderInput.bind(this);
        this.rangeInput.addEventListener('input', this.handleSliderInput);
        this.handleSliderInput();
      }

      this.submitBtn = this.querySelector('[data-scroll-to-contact]');
      if (this.submitBtn) {
        this.handleSubmitClick = this.handleSubmitClick.bind(this);
        this.submitBtn.addEventListener('click', this.handleSubmitClick);
      }
    }

    disconnectedCallback() {
      if (this.rangeInput) {
        this.rangeInput.removeEventListener('input', this.handleSliderInput);
      }
      if (this.submitBtn) {
        this.submitBtn.removeEventListener('click', this.handleSubmitClick);
      }
    }

    handleSliderInput() {
      const index = Number(this.rangeInput.value);
      const amount = this.steps[index];
      if (amount == null) return;

      /*
       * Bonus is set by when the plan is redeemed, not by how much is paid in,
       * so the headline uses the 7th-month rate. The other outcomes are shown
       * by the redemption cards below.
       */
      const bonus = this.bonusRate;
      const paid = amount * this.months;
      const bonusValue = (paid * bonus) / 100;
      const total = paid + bonusValue;

      const maxIndex = this.steps.length - 1;
      const progress = maxIndex > 0 ? index / maxIndex : 0;
      this.style.setProperty('--slider-progress', progress);

      /* CSS derives the arc from this; nothing is styled inline here. */
      const payFraction = total > 0 ? paid / total : 1;
      this.style.setProperty('--pay-fraction', payFraction.toFixed(4));
      /* Midpoint of the bonus arc, in turns, so its label can sit on it. */
      this.style.setProperty('--bonus-mid-turn', ((1 + payFraction) / 2).toFixed(4));

      if (this.amountDisplay) this.amountDisplay.textContent = this.formatAmount(amount);
      if (this.donutPay) this.donutPay.textContent = this.formatAmount(paid);
      if (this.totalPaid) this.totalPaid.textContent = this.formatAmount(paid);
      if (this.bonusAmount) this.bonusAmount.textContent = this.formatAmount(bonusValue);
      if (this.grandTotal) this.grandTotal.textContent = this.formatAmount(total);

      if (this.bonusTitle) {
        const { label = '' } = this.bonusTitle.dataset;
        this.bonusTitle.textContent = `${bonus}% ${label}`.trim();
      }

      if (this.bonusNote) {
        const { template = '' } = this.bonusNote.dataset;
        /* Bonus restated as instalments, e.g. 25% of 6 months = 1.5. */
        const equivalent = this.trimZeros((this.months * bonus) / 100);
        this.bonusNote.textContent = `(${template.replace('[months]', equivalent)})`;
      }

      this.updateDonutWedge(bonus, bonusValue);
      this.updateRedemption(amount);
    }

    updateDonutWedge(bonus, bonusValue) {
      const pct = this.querySelector('[data-donut-bonus-pct]');
      const value = this.querySelector('[data-donut-bonus-value]');
      if (pct) {
        const { label = '' } = pct.dataset;
        pct.textContent = `${label} ${bonus}%`.trim();
      }
      if (value) value.textContent = this.formatAmount(bonusValue);
    }

    updateRedemption(amount) {
      for (const card of this.redeemCards) {
        const cardBonus = Number(card.dataset.bonus ?? 0);
        const instalments = Number(card.dataset.instalments ?? this.months);
        const cardPaid = amount * instalments;

        const pct = card.querySelector('[data-redeem-pct]');
        const paidEl = card.querySelector('[data-redeem-paid]');
        const note = card.querySelector('[data-redeem-instalments]');

        if (pct) pct.textContent = `${100 + cardBonus}%`;
        if (paidEl) paidEl.textContent = this.formatAmount(cardPaid);
        if (note) note.textContent = `(${instalments} Instalments)`;
      }
    }

    trimZeros(n) {
      return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
    }

    /*
     * These are calculator projections, not Shopify prices, so there is no
     * money filter to defer to — the figures only exist once the slider moves.
     */
    formatAmount(n) {
      return `₹${Math.round(n).toLocaleString('en-IN')}`;
    }

    handleSubmitClick(event) {
      event.preventDefault();

      const contactForm = document.querySelector('emi-contact-form');
      if (!contactForm) return;

      contactForm.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const enquirySelect = contactForm.querySelector('#EmiContact-enquiry');
      if (!enquirySelect) return;

      for (const option of enquirySelect.options) {
        if (option.value === 'Money Multiplier Plan Enquiry') {
          enquirySelect.value = option.value;
          break;
        }
      }
    }
  }

  customElements.define('money-multiplier-calculator', MoneyMultiplierCalculator);
}
